import {
    Component,
    computed,
    inject,
    signal
} from '@angular/core';

import {
    RouterLink,
    RouterLinkActive
} from '@angular/router';

import {
    NzIconModule
} from 'ng-zorro-antd/icon';

import {
    SessionService
} from '../../core/auth/services/session.service';

import {
    RestaurantContextService
} from '../../core/restaurant/services/restaurant-context.service';

import {
    MyRestaurantResponse
} from '../../core/restaurant/models/my-restaurant-response';

import {
    BranchResponse
} from '../../core/restaurant/models/branch-response';


interface MenuItem {
    label: string;
    icon: string;
    route?: string;
    section?: boolean;
}


@Component({
    selector: 'app-sidebar',

    imports: [
        RouterLink,
        RouterLinkActive,
        NzIconModule
    ],

    templateUrl: './sidebar.html',
    styleUrl: './sidebar.css'
})
export class Sidebar {

    /*
     * =========================================================
     * SERVICES
     * =========================================================
     */

    private readonly session =
        inject(SessionService);

    private readonly restaurantContext =
        inject(
            RestaurantContextService
        );


    /*
     * =========================================================
     * USER
     * =========================================================
     */

    readonly displayName =
        this.session.displayName;


    readonly primaryRole =
        computed(() => {

            const roles =
                this.session.roles();


            if (
                roles.includes(
                    'PLATFORM_ADMIN'
                )
            ) {
                return 'Administrador';
            }


            if (
                roles.includes(
                    'OWNER'
                )
            ) {
                return 'Propietario';
            }


            if (
                roles.includes(
                    'MANAGER'
                )
            ) {
                return 'Gerente';
            }


            if (
                roles.includes(
                    'CASHIER'
                )
            ) {
                return 'Cajero';
            }


            if (
                roles.includes(
                    'KITCHEN'
                )
            ) {
                return 'Cocina';
            }


            if (
                roles.includes(
                    'BAR'
                )
            ) {
                return 'Bar';
            }


            if (
                roles.includes(
                    'STAFF'
                )
            ) {
                return 'Personal';
            }


            if (
                roles.includes(
                    'CUSTOMER'
                )
            ) {
                return 'Cliente';
            }


            return 'Usuario';
        });


    readonly initials =
        computed(() => {

            const name =
                this.displayName()
                    .trim();


            if (!name) {
                return 'U';
            }


            const parts =
                name.split(/\s+/);


            if (
                parts.length === 1
            ) {

                return parts[0]
                    .substring(0, 2)
                    .toUpperCase();
            }


            return (
                parts[0][0] +
                parts[
                    parts.length - 1
                ][0]
            ).toUpperCase();
        });


    /*
     * =========================================================
     * RESTAURANTS
     * =========================================================
     */

    readonly restaurants =
        this.restaurantContext
            .restaurants;


    readonly selectedRestaurant =
        this.restaurantContext
            .selectedRestaurant;


    readonly restaurantsLoading =
        this.restaurantContext
            .loading;


    readonly restaurantError =
        this.restaurantContext
            .error;


    readonly restaurantName =
        computed(() => {

            if (
                this.restaurantsLoading()
            ) {

                return 'Cargando...';
            }


            return this
                .selectedRestaurant()
                ?.name
                ?? 'Sin restaurante';
        });


    readonly restaurantStatus =
        computed(() => {

            const status =
                this.selectedRestaurant()
                    ?.status;


            switch (status) {

                case 'ACTIVE':
                    return 'Activo';

                case 'TRIAL':
                    return 'Periodo de prueba';

                case 'PENDING':
                    return 'Pendiente';

                case 'SUSPENDED':
                    return 'Suspendido';

                case 'CLOSED':
                    return 'Cerrado';

                default:
                    return '';
            }
        });


    /*
     * =========================================================
     * BRANCHES
     * =========================================================
     */

    readonly branches =
        this.restaurantContext
            .branches;


    readonly selectedBranch =
        this.restaurantContext
            .selectedBranch;


    readonly branchesLoading =
        this.restaurantContext
            .branchesLoading;


    readonly branchName =
        computed(() => {

            if (
                this.branchesLoading()
            ) {

                return 'Cargando sucursales...';
            }


            return this
                .selectedBranch()
                ?.name
                ?? 'Sin sucursal';
        });


    /*
     * =========================================================
     * SELECTOR STATE
     * =========================================================
     */

    readonly restaurantMenuOpen =
        signal(false);


    readonly branchMenuOpen =
        signal(false);


    toggleRestaurantMenu(): void {

        if (
            this.restaurantsLoading()
        ) {
            return;
        }


        this.restaurantMenuOpen
            .update(
                value => !value
            );


        this.branchMenuOpen.set(
            false
        );
    }


    toggleBranchMenu(): void {

        if (
            this.branchesLoading() ||
            !this.selectedRestaurant()
        ) {
            return;
        }


        this.branchMenuOpen
            .update(
                value => !value
            );


        this.restaurantMenuOpen.set(
            false
        );
    }


    selectRestaurant(
        restaurant:
            MyRestaurantResponse
    ): void {

        this.restaurantContext
            .selectRestaurant(
                restaurant
            );


        this.restaurantMenuOpen.set(
            false
        );
    }


    selectBranch(
        branch:
            BranchResponse
    ): void {

        this.restaurantContext
            .selectBranch(
                branch
            );


        this.branchMenuOpen.set(
            false
        );
    }


    /*
     * =========================================================
     * MENU
     * =========================================================
     */

    readonly menuItems: MenuItem[] = [

        {
            label: 'PRINCIPAL',
            icon: '',
            section: true
        },

        {
            label: 'Dashboard',
            icon: 'dashboard',
            route: '/dashboard'
        },


        {
            label: 'GESTIÓN',
            icon: '',
            section: true
        },

        {
            label: 'Restaurante',
            icon: 'shop',
            route: '/restaurants'
        },

        {
            label: 'Sucursales',
            icon: 'environment',
            route: '/branches'
        },

        {
            label: 'Áreas',
            icon: 'layout',
            route: '/dining-areas'
        },

        {
            label: 'Mesas',
            icon: 'appstore',
            route: '/dining-tables'
        },

        {
            label: 'Carta y productos',
            icon: 'unordered-list',
            route: '/menu'
        },

        {
            label: 'Personal',
            icon: 'team',
            route: '/staff'
        },

        {
            label: 'Reservas',
            icon: 'calendar',
            route: '/reservations'
        }

    ];
}